import { useEffect, useRef, useState } from "react";
import { useCoverPin } from "@/hooks/use-cover-pin";
import WaveBackdrop from "./WaveBackdrop";
import { useDragMarquee } from "@/hooks/use-drag-marquee";
import tomHeadshot from "@/assets/testimonials/tom.webp";
import scottHeadshot from "@/assets/testimonials/scott.jpg";
import tylerHeadshot from "@/assets/testimonials/tyler.jpeg";
import melissaHeadshot from "@/assets/testimonials/melissa.jpeg";
import mitchHeadshot from "@/assets/testimonials/mitch.jpeg";
import jackHeadshot from "@/assets/testimonials/jack.jpeg";
import { ZONE_SEQUENCE_DESKTOP, ZONE_SEQUENCE_MOBILE, pickZone } from "./membersZones";

const TEAL = "#44C2E3";

const PULL_QUOTES = [
  "...equivalent to a full-time media director at half the cost.",
  "Fivestar has been a relief.",
  "Takes all the difficult logistic challenges off our plate.",
  "Very organized and efficient. Quality content. Portal easy to use.",
  "Huge Fivestar fans.",
  "Having on-site social management is huge.",
  "Fivestar helps make that happen.",
  "...high-quality content on quick turnarounds.",
  "...removed friction of finding a creator.",
  "A game-changer for our program.",
];



// Bar count scales with the rendered text width so density stays roughly
// constant across short and long quotes (target ~22px per bar).
const TARGET_BAR_SPACING_PX = 22;
const buildBars = (count: number) =>
  Array.from({ length: count }, (_, i) => {
    const s1 = Math.sin(i * 2.31) * 0.5 + 0.5;
    const s2 = Math.sin(i * 1.73 + 17) * 0.5 + 0.5;
    const s3 = Math.sin(i * 3.07 + 41) * 0.5 + 0.5;
    return {
      height: Math.round(28 + s1 * 70),
      delay: parseFloat((s2 * 1.4).toFixed(2)),
      duration: parseFloat((1.0 + s3 * 0.9).toFixed(2)),
    };
  });

// Origin cluster - a punchier 3-bar group at the typing-start point.
const ORIGIN_BARS = [
  { height: 26, delay: 0,    duration: 1.2 },
  { height: 42, delay: 0.18, duration: 1.4 },
  { height: 30, delay: 0.32, duration: 1.05 },
];

const QUOTE_BAR_CSS = `
  @keyframes fq-bar-pulse {
    0%, 100% { transform: scaleY(0.3); }
    50% { transform: scaleY(1); }
  }
  .fq-bar {
    width: 3px;
    background: ${TEAL};
    border-radius: 2px;
    transform-origin: center;
    flex: 0 0 auto;
    animation-name: fq-bar-pulse;
    animation-duration: var(--pulse-duration, 1.2s);
    animation-timing-function: ease-in-out;
    animation-delay: var(--pulse-delay, 0s);
    animation-iteration-count: infinite;
  }
  .fq-origin-bar {
    width: 5px;
    background: ${TEAL};
    border-radius: 3px;
    transform-origin: center;
    animation: fq-bar-pulse 1.2s ease-in-out infinite;
    box-shadow: 0 0 10px rgba(68, 194, 227, 0.55);
    flex: 0 0 auto;
  }
  @media (prefers-reduced-motion: reduce) {
    .fq-bar, .fq-origin-bar { animation: none !important; transform: none !important; }
  }
`;

interface Testimonial {
  headshot: string;
  quote: string;
  name: string;
  role?: string;
  org: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    headshot: tomHeadshot,
    name: "Tom Zummo",
    role: "Founder",
    org: "Legacy Lacrosse",
    quote: "Fivestar has been a relief. Having the ability to book videographers for every event for the entire year gives us such a relief. No more stressing over content and hiring creators. Fivestar makes everything more functional and accessible.",
  },
  {
    headshot: scottHeadshot,
    name: "Scott Kelly",
    role: "Managing Partner",
    org: "ADVNC Seattle Starz",
    quote: "We love the turnkey and dependable video and photo solution Fivestar provides for our club. With social media exposure so important to our club's reputation and our player and family experience, Fivestar takes all the difficult logistic challenges for our club that travels out of state for virtually every tournament we attend.",
  },
  {
    headshot: tylerHeadshot,
    name: "Tyler Kennelly",
    role: "VP of Business Development",
    org: "True Lacrosse",
    quote: "Fivestar has been instrumental in elevating our content game. Our goal is to deliver a platform that showcases our athletes in a meaningful way, and we've found a partner that executes that vision. True Lacrosse helps players reach their full potential, and Fivestar helps make that happen.",
  },
  {
    headshot: melissaHeadshot,
    name: "Melissa Nash",
    role: "Director",
    org: "Utah Elevate Lacrosse",
    quote: "Fivestar has been a game-changer for our program. The connection with great content creators at out-of-state tournaments takes so much off our plate. We appreciate the organization and coordination over our 3-team program, high-level communication, and product we get back. Huge Fivestar fans.",
  },
  {
    headshot: mitchHeadshot,
    name: "Mitch Belisle",
    org: "Trilogy Lacrosse",
    quote: "Fivestar has made it easy to connect with great content creators, especially when we have multiple events going on or are in new regions. They make things simple and have a great team that is always a pleasure to work with.",
  },
  {
    headshot: jackHeadshot,
    name: "Jack Giddens",
    org: "Aloha Tournaments",
    quote: "We love that we were able to customize our membership to get the most value out of it, prioritizing what is important to us. Having on-site social management is huge to be able to produce content during tournament weekends without having to worry about it.",
  },
];

/**
 * Fisher–Yates shuffle so the testimonial cards load in a different order each
 * visit (mirrors the client-logo marquee in TrustedByAndIntro). Returns a new
 * array; the source TESTIMONIALS is left untouched.
 */
const shuffleTestimonials = (arr: Testimonial[]): Testimonial[] => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

type Phase = "typing" | "holding" | "fading";

const FloatingQuoteStage = () => {
  const stageRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<Phase>("typing");
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [seqIndex, setSeqIndex] = useState(0);
  const [running, setRunning] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [measuredQuoteWidth, setMeasuredQuoteWidth] = useState(0);
  const [stageWidth, setStageWidth] = useState(0);

  useEffect(() => {
    setPrefersReducedMotion(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    );
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const node = stageRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(([entry]) => {
      setRunning(entry.isIntersecting);
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, [prefersReducedMotion]);

  // Measure stage width so quote positions can be clamped to fit.
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const update = () => setStageWidth(node.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || !running) return;

    let alive = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const quote = PULL_QUOTES[quoteIndex];
    let charIndex = 1;

    setText("");
    setPhase("typing");

    const advance = () => {
      if (!alive) return;
      const seqLen = isMobile
        ? ZONE_SEQUENCE_MOBILE.length
        : ZONE_SEQUENCE_DESKTOP.length;
      setSeqIndex((s) => (s + 1) % seqLen);
      setQuoteIndex((q) => (q + 1) % PULL_QUOTES.length);
    };

    const startFade = () => {
      if (!alive) return;
      setPhase("fading");
      timeoutId = setTimeout(() => {
        if (!alive) return;
        timeoutId = setTimeout(advance, 400);
      }, 600);
    };

    const hold = () => {
      if (!alive) return;
      setPhase("holding");
      timeoutId = setTimeout(startFade, 2000);
    };

    const typeNext = () => {
      if (!alive) return;
      setText(quote.slice(0, charIndex));
      if (charIndex >= quote.length) {
        hold();
      } else {
        charIndex++;
        timeoutId = setTimeout(typeNext, 50 + Math.random() * 20);
      }
    };

    timeoutId = setTimeout(typeNext, 30);

    return () => {
      alive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [quoteIndex, running, prefersReducedMotion, isMobile]);

  // Measure the full quote's rendered width so the bars row can match it exactly.
  // Declared before the early return below to satisfy rules-of-hooks; it no-ops
  // in reduced-motion mode where measureRef is never attached.
  useEffect(() => {
    if (measureRef.current) {
      setMeasuredQuoteWidth(measureRef.current.offsetWidth);
    }
  }, [quoteIndex, isMobile]);

  if (prefersReducedMotion) {
    return (
      <div
        ref={stageRef}
        className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl mx-auto px-6"
      >
        {PULL_QUOTES.map((q) => (
          <p
            key={q}
            className="text-2xl text-black font-light italic leading-snug"
          >
            <span style={{ color: TEAL }}>{"“"}</span>
            {q}
            <span style={{ color: TEAL }}>{"”"}</span>
          </p>
        ))}
      </div>
    );
  }

  const zone = pickZone(isMobile, seqIndex);
  const fullyTyped = text === PULL_QUOTES[quoteIndex];

  const ORIGIN_GUTTER_PX = isMobile ? 28 : 36;
  const fullQuote = PULL_QUOTES[quoteIndex] || "";
  const typingProgress =
    fullQuote.length > 0 ? text.length / fullQuote.length : 0;

  const barCount = Math.max(
    12,
    Math.min(90, Math.floor(measuredQuoteWidth / TARGET_BAR_SPACING_PX)),
  );
  const bars = buildBars(barCount);

  return (
    <div
      ref={stageRef}
      className="relative w-full mx-auto"
      style={{
        height: isMobile ? "20vh" : "10vh",
        maxWidth: isMobile ? "100%" : "min(95vw, 1800px)",
      }}
    >
      <style>{QUOTE_BAR_CSS}</style>
      {(() => {
        // Clamp desired leftPct so the quote fits within stage width.
        // Before stage is measured, fall back to plain percent positioning.
        const RIGHT_MARGIN_PX = 16;
        const desiredLeftPx = (zone.leftPct / 100) * stageWidth;
        const maxLeftPx = Math.max(
          0,
          stageWidth - measuredQuoteWidth - RIGHT_MARGIN_PX,
        );
        const actualLeft =
          stageWidth > 0
            ? `${Math.min(desiredLeftPx, maxLeftPx)}px`
            : `${zone.leftPct}%`;
        const maxWidth =
          stageWidth > 0
            ? `${Math.max(0, stageWidth - (parseFloat(actualLeft) || 0) - RIGHT_MARGIN_PX)}px`
            : `${100 - zone.leftPct}%`;
        return (
          <div
            className="absolute"
            style={{
              top: zone.topPct !== undefined ? `${zone.topPct}%` : undefined,
              bottom:
                zone.bottomPct !== undefined ? `${zone.bottomPct}%` : undefined,
              left: actualLeft,
              maxWidth,
              opacity: phase === "fading" ? 0 : 1,
              transition: "opacity 600ms ease-out",
            }}
          >
        <div className="relative">
          {/* Hidden measurement: renders the full quote with identical typography so
              we can read its exact pixel width and size the bars row to match. */}
          <span
            ref={measureRef}
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              visibility: "hidden",
              pointerEvents: "none",
              whiteSpace: "nowrap",
              fontFamily: "Aeonik, sans-serif",
              fontStyle: "italic",
              fontWeight: 300,
              fontSize: isMobile ? "26px" : "44px",
              letterSpacing: "-0.01em",
            }}
          >
            {"“"}
            {/* FIV-657: own element, so the measurement follows the current quote even after a translator swapped the text node. */}
            <span>{fullQuote}</span>
            {"”"}
          </span>

          {/* Background bars row.
              Outer is sized by the wrapper (= currently rendered text width) and
              clips overflow, so as text types the outer grows and reveals more of
              the inner. Inner has explicit width = full quote's rendered width,
              with bars distributed via space-between to span exactly that width. */}
          <div
            aria-hidden="true"
            className="absolute pointer-events-none overflow-hidden"
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              left: `${ORIGIN_GUTTER_PX}px`,
              right: 0,
              height: "110px",
              opacity: 0.15,
              zIndex: 0,
            }}
          >
            <div
              className="flex items-center h-full"
              style={{
                width: `${measuredQuoteWidth}px`,
                justifyContent: "space-between",
              }}
            >
              {bars.map((b, i) => {
                // Each bar fades as the cursor passes it. Position p (0-1)
                // across the bar row; bar fades over the next FADE_RANGE of
                // typing progress after the cursor reaches it.
                const p = bars.length > 1 ? i / (bars.length - 1) : 0;
                const FADE_RANGE = 0.22;
                let barOpacity = 1;
                if (typingProgress > p) {
                  barOpacity = Math.max(
                    0,
                    1 - (typingProgress - p) / FADE_RANGE,
                  );
                }
                // Phase change to holding/fading drives any still-visible bars to 0.
                const finalOpacity =
                  phase === "typing" ? barOpacity : 0;
                return (
                  <div
                    key={i}
                    className="fq-bar"
                    style={{
                      height: `${b.height}px`,
                      ["--pulse-delay" as string]: `${b.delay}s`,
                      ["--pulse-duration" as string]: `${b.duration}s`,
                      opacity: finalOpacity,
                      transition:
                        phase === "typing"
                          ? "opacity 180ms ease-out"
                          : "opacity 1500ms ease-out",
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Origin cluster - punchier glow bars at the typing start */}
          <div
            aria-hidden="true"
            className="absolute pointer-events-none flex items-center"
            style={{
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              gap: "5px",
              zIndex: 1,
            }}
          >
            {ORIGIN_BARS.map((b, i) => (
              <div
                key={i}
                className="fq-origin-bar"
                style={{
                  height: `${b.height}px`,
                  animationDelay: `${b.delay}s`,
                  animationDuration: `${b.duration}s`,
                }}
              />
            ))}
          </div>

          <p
            data-testid="floating-quote"
            className="relative text-left font-light italic text-black"
            style={{
              fontSize: isMobile ? "26px" : "44px",
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              paddingLeft: `${ORIGIN_GUTTER_PX}px`,
              zIndex: 2,
            }}
          >
            <span style={{ color: TEAL }}>{"“"}</span>
            {/* FIV-657: own element, so React never deletes a bare text node a translator may have moved. */}
            <span>{text}</span>
            <span
              aria-hidden="true"
              style={{
                color: TEAL,
                opacity: phase === "fading" ? 0 : 0.7,
                marginLeft: "0.05em",
              }}
              className="inline-block animate-pulse"
            >
              |
            </span>
            {fullyTyped && (
              <span style={{ color: TEAL }}>{"”"}</span>
            )}
          </p>
        </div>
      </div>
        );
      })()}
    </div>
  );
};

const TestimonialCard = ({ t }: { t: Testimonial }) => (
  <article
    data-testid="testimonial-card"
    className="flex-none w-[85vw] max-w-[360px] md:w-[760px] md:max-w-none flex flex-col gap-6 select-none bg-white rounded-2xl border border-zinc-200 shadow-[0_4px_30px_rgba(0,0,0,0.04)] p-5 md:p-8"
  >
    <p className="text-[18px] leading-[1.55] text-zinc-800 font-normal">
      <span style={{ color: TEAL }}>{"“"}</span>
      {t.quote}
      <span style={{ color: TEAL }}>{"”"}</span>
    </p>
    <div className="mt-auto pt-2 flex items-center gap-3">
      <img
        src={t.headshot}
        alt={t.name}
        className="w-12 h-12 rounded-full object-cover shrink-0"
        loading="lazy"
        draggable={false}
      />
      <div className="min-w-0">
        <div className="text-[15px] text-black font-medium leading-tight">
          {t.name}
        </div>
        {t.role && (
          <div className="text-[15px] text-zinc-500 font-normal leading-tight mt-0.5">
            {t.role}
          </div>
        )}
      </div>
      <div className="ml-auto shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-zinc-200/70 text-[11px] uppercase tracking-[0.18em] font-medium text-zinc-700">
        {t.org}
      </div>
    </div>
  </article>
);

const TestimonialScroll = () => {
  const { containerRef, trackRef, prefersReducedMotion, handlers } =
    useDragMarquee();
  // Shuffle once per mount so the card order varies between page loads
  // (mirrors the client-logo marquee).
  const [testimonials] = useState(() => shuffleTestimonials(TESTIMONIALS));

  if (prefersReducedMotion) {
    return (
      <div
        ref={containerRef}
        className="overflow-x-auto"
        style={{ scrollbarWidth: "thin" }}
      >
        <div className="flex items-stretch gap-24 px-6 pb-4">
          {testimonials.map((t) => (
            <TestimonialCard key={t.name} t={t} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative cursor-grab active:cursor-grabbing"
      {...handlers}
    >
      <div
        ref={trackRef}
        className="flex items-stretch gap-5 md:gap-24 will-change-transform"
        style={{ width: "max-content" }}
      >
        {testimonials.map((t) => (
          <TestimonialCard key={`a-${t.name}`} t={t} />
        ))}
        {testimonials.map((t) => (
          <TestimonialCard key={`b-${t.name}`} t={t} />
        ))}
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40"
        style={{
          background:
            "linear-gradient(to right, #fff 0%, rgba(255,255,255,0) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40"
        style={{
          background:
            "linear-gradient(to left, #fff 0%, rgba(255,255,255,0) 100%)",
        }}
      />
    </div>
  );
};

// Hero stat: counts up to 99% once scrolled into view (respects reduced
// motion). The large outline numeral matches the big-format numbers used
// elsewhere on the site, in black since this section sits on a light surface.
const RenewalStat = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(99);
      return;
    }
    let raf = 0;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        const DURATION = 1600;
        let start = 0;
        const step = (now: number) => {
          if (!start) start = now;
          const t = Math.min(1, (now - start) / DURATION);
          const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
          setValue(Math.round(eased * 99));
          if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.6 },
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-label="99%"
      className="text-center text-[5rem] md:text-[9rem] leading-none font-extrabold not-italic text-transparent select-none"
      style={{ WebkitTextStroke: "2px #000" }}
    >
      {value}%
    </div>
  );
};

const MembersSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const coverTop = useCoverPin(sectionRef);

  return (
    <section
      ref={sectionRef}
      className="relative md:sticky overflow-hidden pt-14 pb-14 sm:pt-20 sm:pb-20 md:pt-32 md:pb-32"
      style={{
        background: "linear-gradient(to bottom, #f4f4f5 0%, #ffffff 320px)",
        top: coverTop,
      }}
    >
      {/* Animated synth-wave backdrop (shared with the Why-Fivestar section). */}
      <WaveBackdrop />

      <div className="relative">
        <div className="px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <RenewalStat />
            <h2 className="mt-3 md:mt-4 text-center text-3xl md:text-5xl font-black italic uppercase leading-[1.05] text-black">
              of Fivestar Members Renewed Last Year
            </h2>
            <p className="mt-5 md:mt-6 text-center text-base md:text-lg font-normal text-zinc-600 leading-relaxed">
              Behind every renewal sits a vetted creator at every event, premium content delivered on time, and a platform that compounds year after year, and the receipts speak for themselves.
            </p>
          </div>
        </div>

        <div className="mt-12 px-6 md:px-10">
          <FloatingQuoteStage />
        </div>

        <div className="mt-12">
          <TestimonialScroll />
        </div>
      </div>
    </section>
  );
};

export default MembersSection;
